/**
 * Typed event vocabulary for Phase 1 — shared between apps/api and apps/web so
 * event names cannot drift across the workspace boundary (MED-8 equivalent for
 * the in-process bus).
 *
 * The bus (apps/api/src/events/bus.ts) validates every emit against the Zod
 * schema named here. Handlers receive the parsed payload.
 *
 * Plans that will register handlers on these events:
 *  - Plan 05 (WS hub):        broadcasts push.* on its topic
 *    (member.* was declared but never emitted; removed in #148)
 *  - Plan 06 (push):          listens to push.subscription.expired, weather.*
 *  - Plan 07 (weather/sun):   emits weather.refreshed, weather.circuit_tripped, sun.refreshed
 *  - Plan 08 (onboarding):    emits onboarding.completed
 *  - Plan 15 (ForceReload):   emits system.reload
 *
 * Every new event in Phase 2+ (chore.*, point.*, shopping.*, etc.) MUST be
 * added to this map; the EventName type is derived and the bus rejects
 * unknown names at compile time.
 */
import { z } from 'zod';
import { ShoppingItemSchema } from './shopping/messages';
import { aiEventSchemas } from './ai/events';

export const eventSchemas = {
  // ── Phase 1 ────────────────────────────────────────────────────────────
  'push.sent': z.object({ familyId: z.string().min(1), memberId: z.string(), endpoint: z.string() }),
  'push.subscription.expired': z.object({ familyId: z.string().min(1), endpoint: z.string() }),
  'weather.refreshed': z.object({ coordKey: z.string(), fromCache: z.boolean() }),
  'weather.circuit_tripped': z.object({
    coordKey: z.string(),
    statusCode: z.number(),
  }),
  'sun.refreshed': z.object({ coordKey: z.string(), date: z.string() }),
  'system.reload': z.object({}),
  // FF-11 (#179): familieadminen har fullført oppsettet av familien.
  'onboarding.completed': z.object({ familyId: z.string().min(1), adminMemberId: z.string() }),
  // FF-11 (#179): familien er suspendert eller slettet av systemadminen.
  // auth.subscribers lukker familiens sockets (hub.closeFamily, 4401) etter commit.
  'family.closed': z.object({
    familyId: z.string().min(1),
    reason: z.enum(['suspended', 'deleted']),
  }),

  // ── Phase 2 — Chore Engagement Loop (D-14, 16 entries) ───────────────
  // Every payload is small, verb-only, no PII. Members are referenced by id
  // (never by name/email). Phase 1 P05 decision: zod v4 — use `z.iso.datetime()`.
  // `familyId` (#171, Pattern 9): abonnentene, push og WS-huben bruker familien
  // hendelsen gjelder i stedet for å slå den opp igjen. Påkrevd — se
  // events.family.test.ts. `weekly.closed` gjelder én families uke.
  'chore.spawned':     z.object({ familyId: z.string().min(1), instanceId: z.string(), choreId: z.string(), spawnKey: z.string() }),
  'chore.claimed':     z.object({ familyId: z.string().min(1), instanceId: z.string(), claimedBy: z.string(), claimExpiresAt: z.iso.datetime() }),
  'chore.completed':   z.object({ familyId: z.string().min(1), instanceId: z.string(), completedBy: z.string(), hasProof: z.boolean() }),
  // `prevBalance` is the claimer's ledger SUM captured INSIDE approve()'s
  // transaction, BEFORE the award row is appended — it is what drives the L3
  // count-up on the kid's wallet (issue #79). Required, not optional: the bus
  // Zod-parses on emit, so a caller that forgets it throws at the emit site
  // instead of silently degrading to a dead celebration, which is the exact
  // failure mode this field exists to fix. Not `.nonnegative()` — a manual
  // adjustment can legitimately leave a balance below zero.
  'chore.approved':    z.object({ familyId: z.string().min(1), instanceId: z.string(), approvedBy: z.string(), claimerId: z.string(), points: z.number().int(), prevBalance: z.number().int() }),
  'chore.rejected':    z.object({ familyId: z.string().min(1), instanceId: z.string(), claimerId: z.string(), reason: z.string() }),
  'chore.expired':     z.object({ familyId: z.string().min(1), instanceId: z.string(), returnedToPool: z.boolean() }),
  // Parent-initiated instance control (parent console). All three are admin
  // actions on a live instance and carry the acting admin for the audit trail.
  'chore.reassigned':  z.object({ familyId: z.string().min(1), instanceId: z.string(), memberId: z.string(), byAdmin: z.string() }),
  'chore.cancelled':   z.object({ familyId: z.string().min(1), instanceId: z.string(), byAdmin: z.string() }),
  'chore.restored':    z.object({ familyId: z.string().min(1), instanceId: z.string(), byAdmin: z.string() }),
  'point.earned':      z.object({ familyId: z.string().min(1), memberId: z.string(), amount: z.number().int().positive(), choreInstanceId: z.string() }),
  'point.spent':       z.object({ familyId: z.string().min(1), memberId: z.string(), amount: z.number().int().negative(), rewardClaimId: z.string() }),
  // Manual correction or bonus by a parent. `amount` is signed and non-zero;
  // `note` is required at the route so the ledger never carries an unexplained
  // adjustment.
  'point.adjusted':    z.object({ familyId: z.string().min(1), memberId: z.string(), amount: z.number().int(), byAdmin: z.string(), note: z.string() }),
  'streak.updated':    z.object({ familyId: z.string().min(1), memberId: z.string(), currentStreak: z.number().int().nonnegative(), longestStreak: z.number().int().nonnegative() }),
  'streak.broken':     z.object({ familyId: z.string().min(1), memberId: z.string() }),
  'streak.frozen':     z.object({ familyId: z.string().min(1), memberId: z.string(), balanceAfter: z.number().int().nonnegative() }),
  'achievement.unlocked': z.object({ familyId: z.string().min(1), memberId: z.string(), key: z.string(), unlockedAt: z.iso.datetime() }),
  'reward.claimed':    z.object({ familyId: z.string().min(1), rewardClaimId: z.string(), memberId: z.string(), rewardId: z.string(), pointCost: z.number().int().positive() }),
  'reward.approved':   z.object({ familyId: z.string().min(1), rewardClaimId: z.string(), memberId: z.string(), points: z.number().int().negative() }),
  'reward.rejected':   z.object({ familyId: z.string().min(1), rewardClaimId: z.string(), memberId: z.string(), reason: z.string() }),
  'weekly.closed':     z.object({ familyId: z.string().min(1), weekTag: z.string().regex(/^\d{4}-W\d{2}$/), winnerMemberId: z.string().nullable(), totalPoints: z.number().int().nonnegative() }),

  // ── Phase 3 — Family Calendar (D-13, 5 entries) ───────────────────────
  // Verb-only broadcasts (Pattern 3); reminder.* are server-internal audit only.
  // `familyId` (#172): abonnenten sender bare til familien hendelsen tilhører.
  'event.created':           z.object({ familyId: z.string().min(1), eventId: z.string(), seriesId: z.string(), createdBy: z.string() }),
  'event.updated':           z.object({ familyId: z.string().min(1), eventId: z.string(), seriesId: z.string(), scope: z.enum(['single', 'from_this', 'series']) }),
  'event.deleted':           z.object({ familyId: z.string().min(1), eventId: z.string(), seriesId: z.string(), scope: z.enum(['single', 'from_this', 'series']) }),
  'reminder.dispatched':     z.object({ familyId: z.string().min(1), reminderId: z.string(), eventId: z.string(), memberId: z.string() }),
  'reminder.skipped_stale':  z.object({ familyId: z.string().min(1), reminderId: z.string(), fireAt: z.iso.datetime() }),

  // ── Phase 4 — Shared Shopping List (D-08, 5 entries) ─────────────────
  // D-08 carve-out: full ShoppingItem row in payload (small ~200B rows;
  // saves every connected client a refetch — Success Criterion #1 ≤50ms WS).
  // shopping.removed is the exception — clients drop by id (soft-delete tombstone).
  // `familyId` (#169): abonnentene og WS-huben sender bare til familien raden
  // tilhører. Påkrevd — se events.family.test.ts.
  'shopping.added':     z.object({ familyId: z.string().min(1), id: z.string().regex(/^[0-9a-f-]{36}$/i), addedBy: z.string().regex(/^[0-9a-f-]{36}$/i),    item: ShoppingItemSchema }),
  'shopping.updated':   z.object({ familyId: z.string().min(1), id: z.string().regex(/^[0-9a-f-]{36}$/i), updatedBy: z.string().regex(/^[0-9a-f-]{36}$/i),  item: ShoppingItemSchema }),
  'shopping.checked':   z.object({ familyId: z.string().min(1), id: z.string().regex(/^[0-9a-f-]{36}$/i), checkedBy: z.string().regex(/^[0-9a-f-]{36}$/i),  item: ShoppingItemSchema }),
  'shopping.unchecked': z.object({ familyId: z.string().min(1), id: z.string().regex(/^[0-9a-f-]{36}$/i), uncheckedBy: z.string().regex(/^[0-9a-f-]{36}$/i), item: ShoppingItemSchema }),
  'shopping.removed':   z.object({ familyId: z.string().min(1), id: z.string().regex(/^[0-9a-f-]{36}$/i), removedBy: z.string().regex(/^[0-9a-f-]{36}$/i) }),

  // ── Phase 5 — Family Assistant (4 entries) ───────────────────────────
  ...aiEventSchemas,

  // ── Fase 5 — Chore proposals + Family feed ───────────────────────────
  // Chore-proposal lifecycle events deferred from Fase 3 — wired onto the bus
  // here so the proposal-review queue refreshes live (chores.subscribers) and
  // an approved kid-suggestion materialises a feed item (feed.subscribers).
  // Verb-only, no PII; members referenced by id.
  'chore.proposal.created':  z.object({ familyId: z.string().min(1), choreId: z.string(), proposedBy: z.string(), title: z.string() }),
  'chore.proposal.approved': z.object({ familyId: z.string().min(1), choreId: z.string(), proposedBy: z.string(), approvedBy: z.string(), title: z.string(), points: z.number().int().nonnegative() }),
  'chore.proposal.rejected': z.object({ familyId: z.string().min(1), choreId: z.string(), proposedBy: z.string(), rejectedBy: z.string() }),
  // Family feed (push model). feed.subscribers writes the rows on the source
  // events above + chore.approved / achievement.unlocked / streak.updated and
  // broadcasts 'item_added' inline. The reaction toggle goes route → service →
  // this event → subscriber broadcast (mirrors the shopping write path).
  'feed.reaction.changed':   z.object({ familyId: z.string().min(1), itemId: z.string(), memberId: z.string() }),

  // ── Wave 2 (v2.0a) — Avatar/cosmetic shop (instant points sink) ──────────
  // Verb-only, no PII. `cosmetic.purchased` carries the (negative) point delta
  // so cosmetics.subscribers can mirror reward.approved's 'points'-topic
  // broadcast — the wallet balance updates live with no extra round-trip. NO
  // approval step (the key difference from reward.claimed): a purchase is
  // terminal the instant it is emitted. `cosmetic.equipped` lets other
  // surfaces (idle dashboard avatar) refresh the composited portrait.
  'cosmetic.purchased': z.object({ familyId: z.string().min(1), memberId: z.string(), itemId: z.string(), points: z.number().int().negative() }),
  'cosmetic.equipped':  z.object({ familyId: z.string().min(1), memberId: z.string(), itemId: z.string(), equipped: z.boolean() }),

  // ── Wave 3 (v2.0b) — Meal planner ────────────────────────────────────────
  // Emitted when a meal is confirmed (materialised). feed.subscribers bridges
  // it to a 'meal_planned' family-feed item. Verb-only, no PII; `date` may be
  // null for an undated meal.
  'meal.planned': z.object({ familyId: z.string().min(1), mealId: z.string(), createdBy: z.string(), title: z.string(), date: z.string().nullable() }),

  // ── Innlogging (#142) ────────────────────────────────────────────────────
  // Økter er slettet (utlogging, brukerbytte på kjøkkenskjermen, tilbakekalt
  // enhet, arkivert medlem). auth.subscribers lukker socketene som ble åpnet
  // med dem — huben sjekker økten bare ved oppgradering.
  'auth.sessions_ended': z.object({ sessionIds: z.array(z.string()).min(1) }),
} as const;

export type EventName = keyof typeof eventSchemas;

export type EventPayload<K extends EventName> = z.infer<(typeof eventSchemas)[K]>;

export const EVENT_NAMES = Object.keys(eventSchemas) as readonly EventName[];
